import React, { useState, useEffect } from 'react';
import './CharacterDialog.css';
import { Character} from '../../types/character';
import { charactersAPI } from '../../api/characters';
import CharacterForm from './CharacterForm';

interface CharacterDialogProps {
  onClose: () => void;
  onSelectCharacter: (character: Character) => void;
  selectedCharacters?: Character[];
}

const CharacterDialog: React.FC<CharacterDialogProps> = ({ 
  onClose, 
  onSelectCharacter,
  selectedCharacters = []
}) => {
  const [activeTab, setActiveTab] = React.useState<'characters' | 'illustrator' | 'books'>('characters');
  const [availableCharacters, setAvailableCharacters] = useState<Character[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddingCharacter, setIsAddingCharacter] = useState(false);

  useEffect(() => {
    const loadCharacters = async () => {
      try {
        setIsLoading(true);
        const response = await charactersAPI.list();
        const filteredCharacters = response.data.results.filter(
          character => !selectedCharacters.some(selected => selected.id === character.id)
        );
        setAvailableCharacters(filteredCharacters);
        setError(null);
      } catch (err) {
        setError('Failed to load characters');
        console.error('Error loading characters:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadCharacters();
  }, [selectedCharacters]);

  const handleAddCharacter = async (formData: FormData, callback: () => void) => {
    try {
      const response = await charactersAPI.create(formData);
      const newCharacter = response.data;
      
      await charactersAPI.select(newCharacter.id);
      onSelectCharacter(newCharacter);
      setIsAddingCharacter(false);
      onClose();
    } catch (error) {
      console.error('Error creating character:', error);
    } finally {
      callback();
    }
  };


  const renderCharacterOption = (character: Character) => (
    <div 
      key={character.id}
      className="character-option"
      onClick={() => onSelectCharacter(character)}
    >
      <img 
        src={character.image || '/default-avatar.png'} 
        alt={character.name} 
      />
      <div className="hover-overlay">
        <span className="plus-icon">+</span>
      </div>
      <span>{character.name}</span>
    </div>
  );

  if (isAddingCharacter) {
    return (
      <>
        <div className="character-dialog-backdrop" onClick={onClose}></div>
        <div className="character-dialog">
          <CharacterForm 
            onSubmit={handleAddCharacter}
            onBack={() => setIsAddingCharacter(false)}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="character-dialog-backdrop" onClick={onClose}></div>
      <div className="character-dialog">
        <div className="dialog-header">
          <h2>Add character</h2>
          <button className="close-btn" onClick={onClose}></button>
        </div>

        <div 
          className="create-character-section"
          onClick={() => setIsAddingCharacter(true)}
        >
          <div className="avatar-placeholder">
            <img src="/avatar-placeholder.svg" alt="Create character" />
          </div>
          <h3>Create new character</h3>
        </div>
        
        <div className="character-tabs">
          <button 
            className={`tab ${activeTab === 'characters' ? 'active' : ''}`}
            onClick={() => setActiveTab('characters')}
          >
            Characters
          </button>
        </div>

        <div className="characters-grid">
          {isLoading ? (
            <div className="loading">Loading characters...</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : (
            availableCharacters?.map(character => renderCharacterOption(character))
          )}
        </div>
      </div>
    </>
  );
};

export default CharacterDialog;